"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { devLabel, devLabelPanel } from "@/lib/dev-labels";
import styles from "./PreAnalysisModal.module.css";

/**
 * PreAnalysisModal — Phase 25-AB Task 18 #7 (panel 62 / wc-pa-modal).
 *
 * Confirmation dialog after URL/file input → before Analyze. Shows thumbnail,
 * video metadata (title, channel, language, license), and the analysis segment.
 * User clicks "템플릿 선택 →" to advance to TemplatePreSelectModal.
 *
 * Mockup source: docs/mockups/welcome.html lines 999-1017.
 */
export interface PreAnalysisVideoMeta {
	thumbUrl?: string;
	thumbBg?: string;
	durationLabel?: string;
	title: string;
	channel: string;
	language: string;
	languageConfidence?: number;
	license: string;
	segmentLabel?: string;
}

export interface PreAnalysisConfig {
	whisperLanguage: string;
	segmentLabel?: string;
	bypassAutoCaption: boolean;
}

export interface PreAnalysisModalProps {
	open: boolean;
	meta?: PreAnalysisVideoMeta;
	loading?: boolean;
	onClose: () => void;
	onProceed?: (config: PreAnalysisConfig) => void;
}

/**
 * Honest "metadata not yet fetched" stand-in. The previous DEFAULT_META was
 * a fictional Korean travel vlog ("노을 드라이브") shown for every URL,
 * which made the modal look completely broken to users. Now we surface a
 * clear "메타 가져오는 중..." state until the oembed fetch returns; if the
 * source has no oembed support we leave the fields as "분석 후 확인" so the
 * user knows the data isn't fake.
 */
const LOADING_META: PreAnalysisVideoMeta = {
	durationLabel: "—",
	title: "메타 정보 가져오는 중…",
	channel: "—",
	language: "분석 후 확인",
	license: "분석 후 확인",
	segmentLabel: "00:00 ~ 끝 (전체)",
};

const FALLBACK_META: PreAnalysisVideoMeta = {
	durationLabel: "—",
	title: "(미리보기 메타를 가져오지 못했습니다)",
	channel: "분석은 정상 진행됩니다",
	language: "분석 후 확인",
	license: "분석 후 확인",
	segmentLabel: "00:00 ~ 끝 (전체)",
};

export function PreAnalysisModal({
	open,
	meta,
	loading,
	onClose,
	onProceed,
}: PreAnalysisModalProps) {
	const displayMeta: PreAnalysisVideoMeta = meta ?? (loading ? LOADING_META : FALLBACK_META);
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	useEffect(() => {
		if (!open) return;
		function onKey(e: KeyboardEvent) {
			if (e.key === "Escape") onClose();
		}
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [open, onClose]);

	if (!open || !mounted) return null;

	return createPortal(
		<div
			id="wc-pa-modal"
			className={styles.overlay}
			role="dialog"
			aria-modal="true"
			aria-label="분석 시작 전 확인"
			{...devLabelPanel("62 · PreAnalysisModal")}
		>
			<div className={styles.card}>
				<div className={styles.head}>
					<div className={styles.title}>분석 시작 전 확인</div>
					<button
						type="button"
						className={styles.close}
						onClick={onClose}
						aria-label="닫기"
					>
						×
					</button>
				</div>
				<div
					className={styles.thumb}
					style={
						displayMeta.thumbUrl
							? {
									backgroundImage: `url("${displayMeta.thumbUrl}")`,
									backgroundSize: "cover",
									backgroundPosition: "center",
								}
							: displayMeta.thumbBg
								? { background: displayMeta.thumbBg }
								: undefined
					}
					{...devLabel("62-A")}
				>
					{displayMeta.durationLabel && (
						<div className={styles.thumbDur}>{displayMeta.durationLabel}</div>
					)}
				</div>
				<div className={styles.row} {...devLabel("62-B")}>
					<span className={styles.rowLabel}>제목</span>
					<span style={{ fontWeight: 700 }}>{displayMeta.title}</span>
				</div>
				<div className={styles.row}>
					<span className={styles.rowLabel}>채널 · 조회수</span>
					<span>{displayMeta.channel}</span>
				</div>
				<div className={styles.row}>
					<span className={styles.rowLabel}>언어 (자동)</span>
					<span style={{ color: "#22c55e" }}>
						{displayMeta.language}
						{typeof displayMeta.languageConfidence === "number" &&
							` (${displayMeta.languageConfidence}%)`}
					</span>
				</div>
				<div className={styles.row}>
					<span className={styles.rowLabel}>라이선스</span>
					<span style={{ color: "#4eb3c1" }}>{displayMeta.license}</span>
				</div>
				<div className={styles.row} {...devLabel("62-C")}>
					<span className={styles.rowLabel}>구간 선택</span>
					<span
						style={{
							fontFamily: "'Geist Mono',monospace",
							color: "#d4af37",
						}}
					>
						{displayMeta.segmentLabel ?? "00:00 ~ 끝 (전체)"}
					</span>
				</div>
				<div className={styles.actions}>
					<button type="button" className={styles.cancel} onClick={onClose}>
						취소
					</button>
					<button
						type="button"
						className={styles.proceed}
						onClick={() =>
							onProceed?.({
								whisperLanguage: displayMeta.language,
								segmentLabel: displayMeta.segmentLabel,
								bypassAutoCaption: false,
							})
						}
						{...devLabel("62-D")}
					>
						템플릿 선택 →
					</button>
				</div>
			</div>
		</div>,
		document.body,
	);
}
